import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { hubApi, PLAN_PRICING, STATUS_STYLE, BILLING_PERIOD_LABEL, calcFinalAmount } from "../api.js";

const fmt     = d => d ? new Date(d).toLocaleDateString("ro-RO") : "—";
const fmtLong = d => d ? new Date(d).toLocaleDateString("ro-RO", { day:"numeric", month:"long", year:"numeric" }) : "—";
const fmtCur  = (a, c) => (a != null && a !== "") ? `${Number(a).toLocaleString("ro-RO")} ${c||"EUR"}` : "—";
const toInput = d => d ? new Date(d).toISOString().split("T")[0] : "";

const PLANS        = [1,2,3];
const CURRENCY_OPTS= ["EUR","RON","USD","GBP"];
const STATUS_OPTS  = [{ v:"pending",l:"În așteptare" },{ v:"paid",l:"Plătit" },{ v:"overdue",l:"Restanță" }];

const C = {
  bg:"#f6f8fa", white:"#ffffff", border:"#e3e8ef",
  text:"#0a2540", muted:"#697386", subtle:"#a3acb9",
  indigo:"#635bff", indigoL:"#f0efff",
  green:"#09825d", greenL:"#e3f5ef",
  amber:"#c07000", amberL:"#fff8e6",
  red:"#c0392b", redL:"#fdf2f2",
};

const INP = {
  width:"100%", padding:"9px 12px", borderRadius:7, border:`1px solid #e3e8ef`,
  background:"#fff", fontSize:13, outline:"none", color:"#0a2540",
  transition:"border-color 0.15s",
};

export default function RestaurantPage() {
  const { id } = useParams();
  const nav    = useNavigate();
  const fileRef = useRef();

  const [r, setR]                     = useState(null);
  const [loading, setLoading]         = useState(true);
  const [savingInfo, setSavingInfo]   = useState(false);
  const [savingBill, setSavingBill]   = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [dragging, setDragging]       = useState(false);
  const [noteText, setNoteText]       = useState("");
  const [savingNote, setSavingNote]   = useState(false);
  const [openSection, setOpenSection] = useState(null);

  const [info, setInfo] = useState({
    name:"", slug:"", companyName:"", vatNumber:"", address:"",
    contactPerson:"", contactEmail:"", contactPhone:"",
    backendUrl:"", frontendUrl:"", adminUrl:"",
    plan:1, billingPeriod:"monthly",
    discountType:"none", discountValue:0, discountNote:"", discountExpiry:"",
    contractStart:"", contractEnd:"",
  });

  const [bill, setBill] = useState({
    amount:"", currency:"EUR", dueDate:"", billingStatus:"pending",
  });

  useEffect(() => {
    hubApi.getOne(id)
      .then(({ data }) => {
        if (!data.success) { toast.error("Restaurant negăsit."); return; }
        const x = data.restaurant;
        setR(x);
        setInfo({
          name:x.name||"", slug:x.slug||"", companyName:x.companyName||"",
          vatNumber:x.vatNumber||"", address:x.address||"",
          contactPerson:x.contactPerson||"", contactEmail:x.contactEmail||"",
          contactPhone:x.contactPhone||"",
          backendUrl:x.backendUrl||"", frontendUrl:x.frontendUrl||"", adminUrl:x.adminUrl||"",
          plan:x.plan||1, billingPeriod:x.billingPeriod||"monthly",
          discountType:x.discountType||"none", discountValue:x.discountValue||0,
          discountNote:x.discountNote||"", discountExpiry:toInput(x.discountExpiry),
          contractStart:toInput(x.contractStart), contractEnd:toInput(x.contractEnd),
        });
        setBill({
          amount:x.currentAmount||"", currency:x.currentCurrency||"EUR",
          dueDate:toInput(x.currentDueDate), billingStatus:x.billingStatus||"pending",
        });
      })
      .catch(() => toast.error("Eroare la încărcare."))
      .finally(() => setLoading(false));
  }, [id]);

  const setI = (k,v) => setInfo(p=>({...p,[k]:v}));
  const setB = (k,v) => setBill(p=>({...p,[k]:v}));

  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      const { data } = await hubApi.update(id, info);
      if (data.success) { setR(data.restaurant); toast.success("Informații salvate."); }
      else toast.error(data.message);
    } catch { toast.error("Eroare."); }
    finally { setSavingInfo(false); }
  };

  const handleSaveBilling = async () => {
    setSavingBill(true);
    try {
      let invoiceUrl = r?.currentInvoiceUrl || "";
      if (invoiceFile) {
        const { data: up } = await hubApi.uploadInvoice(id, invoiceFile);
        if (!up.success) { toast.error(up.message); setSavingBill(false); return; }
        invoiceUrl = up.invoiceUrl;
        setInvoiceFile(null);
      }
      const { data } = await hubApi.billing(id, { ...bill, invoiceUrl });
      if (data.success) {
        setR(p => ({ ...p, billingStatus:bill.billingStatus, currentAmount:bill.amount, currentDueDate:bill.dueDate, currentCurrency:bill.currency, currentInvoiceUrl:invoiceUrl }));
        toast.success("Factură salvată.");
      } else toast.error(data.message);
    } catch { toast.error("Eroare la salvare."); }
    finally { setSavingBill(false); }
  };

  const handleToggle = async () => {
    try {
      const { data } = await hubApi.toggle(id);
      if (data.success) setR(p=>({...p, isActive:data.isActive}));
    } catch { toast.error("Eroare."); }
  };

  const handleMarkPaid = async () => {
    try {
      const { data } = await hubApi.markPaid(id);
      if (data.success) {
        setR(p=>({...p, billingStatus:"paid"}));
        setBill(p=>({...p, billingStatus:"paid"}));
        toast.success("Marcat ca plătit.");
      }
    } catch { toast.error("Eroare."); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await hubApi.addNote(id, noteText);
      if (data.success) { setR(p=>({...p, notes:data.notes})); setNoteText(""); }
    } catch { toast.error("Eroare."); }
    finally { setSavingNote(false); }
  };

  const handleDeleteNote = async (nid) => {
    try {
      const { data } = await hubApi.deleteNote(id, nid);
      if (data.success) setR(p=>({...p, notes:data.notes}));
    } catch { toast.error("Eroare."); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f?.type === "application/pdf") setInvoiceFile(f);
    else toast.error("Doar fișiere PDF.");
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"60vh", color:C.muted, gap:10, fontSize:14 }}>
      <Spin color={C.muted}/> Se încarcă...
    </div>
  );
  if (!r) return <div style={{ padding:48, color:C.red }}>Restaurant negăsit.</div>;

  const planObj  = PLAN_PRICING[info.plan] || PLAN_PRICING[1];
  const ss       = STATUS_STYLE[r.billingStatus] || STATUS_STYLE.pending;
  const isOverdue= r.billingStatus === "overdue";
  const isPaid   = r.billingStatus === "paid";
  const finalAmt = calcFinalAmount(info.plan, info.billingPeriod, info.discountType, info.discountValue);

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
      <style>{`
        input,select,textarea { font-family:inherit; }
        input::placeholder,textarea::placeholder { color:#a3acb9; }
        *,*::before,*::after { box-sizing:border-box; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .row-hover:hover { background:#f6f8fa !important; }
      `}</style>

      {/* Top bar */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 32px", height:54, display:"flex", alignItems:"center", gap:16, position:"sticky", top:0, zIndex:10 }}>
        <button onClick={()=>nav("/")} style={{ background:"none", border:"none", cursor:"pointer", color:C.muted, display:"flex", alignItems:"center", gap:5, fontSize:13, fontWeight:500, padding:0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          Restaurante
        </button>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.subtle} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{r.name}</span>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
          {r.adminUrl && (
            <a href={r.adminUrl} target="_blank" rel="noreferrer"
              style={{ padding:"6px 13px", borderRadius:6, border:`1px solid ${C.border}`, background:C.white, color:C.text, fontSize:13, fontWeight:500, textDecoration:"none", display:"flex", alignItems:"center", gap:5 }}>
              Admin <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          )}
          {!isPaid && Number(r.currentAmount) > 0 && (
            <button onClick={handleMarkPaid} style={{ padding:"6px 13px", borderRadius:6, border:"none", background:C.green, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
              ✓ Marchează plătit
            </button>
          )}
          <button onClick={handleToggle}
            style={{ padding:"6px 13px", borderRadius:6, border:`1px solid ${C.border}`, background:C.white, color:r.isActive?C.red:C.green, fontSize:13, fontWeight:600, cursor:"pointer" }}>
            {r.isActive ? "Dezactivează" : "Activează"}
          </button>
        </div>
      </div>

      <div style={{ maxWidth:960, margin:"0 auto", padding:"32px 24px", display:"grid", gridTemplateColumns:"1fr 300px", gap:20, alignItems:"start" }}>

        {/* LEFT */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {isOverdue && (
            <div style={{ padding:"12px 16px", borderRadius:8, background:C.redL, border:`1px solid #f5c6cb`, display:"flex", gap:10, alignItems:"center" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style={{ fontSize:13, color:C.red, fontWeight:500 }}>Factură restantă — contactează clientul.</span>
            </div>
          )}

          {/* CARD: Factură */}
          <Card>
            <CardHeader title="Factură curentă"/>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 110px", gap:12, marginBottom:14 }}>
              <div>
                <Label>Sumă</Label>
                <div style={{ position:"relative" }}>
                  <input type="number" value={bill.amount} onChange={e=>setB("amount",e.target.value)}
                    placeholder={`ex: ${finalAmt}`} style={{ ...INP, paddingRight:52 }}/>
                  <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:12, color:C.muted, fontWeight:600, pointerEvents:"none" }}>{bill.currency}</span>
                </div>
              </div>
              <div>
                <Label>Valută</Label>
                <select value={bill.currency} onChange={e=>setB("currency",e.target.value)} style={INP}>
                  {CURRENCY_OPTS.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:14 }}>
              <Label>Dată scadentă</Label>
              <input type="date" value={bill.dueDate} onChange={e=>setB("dueDate",e.target.value)} style={INP}/>
            </div>

            <div style={{ marginBottom:20 }}>
              <Label>Status plată</Label>
              <div style={{ display:"flex", gap:8 }}>
                {STATUS_OPTS.map(o=>{
                  const s=STATUS_STYLE[o.v]; const active=bill.billingStatus===o.v;
                  return (
                    <button key={o.v} onClick={()=>setB("billingStatus",o.v)}
                      style={{ flex:1, padding:"9px 6px", borderRadius:7, border:`1.5px solid ${active?s.color:C.border}`, background:active?s.bg:C.white, color:active?s.color:C.muted, fontWeight:600, fontSize:12, cursor:"pointer", transition:"all 0.12s" }}>
                      {o.l}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ height:1, background:C.border, marginBottom:20 }}/>

            {/* Sume rapide */}
            <div style={{ marginBottom:20 }}>
              <Label>Sume rapide</Label>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:4 }}>
                {PLANS.map(pid=>["monthly","quarterly","yearly"].map(per=>{
                  const p=PLAN_PRICING[pid]; const a=calcFinalAmount(pid,per,info.discountType,info.discountValue);
                  return (
                    <button key={`${pid}-${per}`} onClick={()=>setBill(b=>({...b,amount:a,currency:"EUR"}))}
                      style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${C.border}`, background:C.white, fontSize:11.5, fontWeight:500, cursor:"pointer", color:C.text }}
                      onMouseEnter={e=>e.currentTarget.style.background=C.bg}
                      onMouseLeave={e=>e.currentTarget.style.background=C.white}>
                      <span style={{ color:p.color, fontWeight:700 }}>{p.name}</span> · €{a} <span style={{ color:C.subtle }}>/{BILLING_PERIOD_LABEL[per]?.toLowerCase()}</span>
                    </button>
                  );
                }))}
              </div>
            </div>

            <div style={{ height:1, background:C.border, marginBottom:20 }}/>

            {/* PDF upload — integrat */}
            <div style={{ marginBottom:20 }}>
              <Label>Factură PDF <span style={{ color:C.subtle, fontWeight:400, fontSize:11 }}>(opțional — se salvează împreună)</span></Label>

              {r.currentInvoiceUrl && !invoiceFile && (
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:7, background:C.greenL, border:`1px solid #b3ddd1`, marginBottom:8 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span style={{ flex:1, fontSize:13, color:C.green, fontWeight:500 }}>Factură activă</span>
                  <a href={r.currentInvoiceUrl} target="_blank" rel="noreferrer" style={{ fontSize:12, color:C.green, fontWeight:700, textDecoration:"none" }}>Descarcă →</a>
                  <button onClick={()=>setR(p=>({...p,currentInvoiceUrl:""}))} style={{ background:"none", border:"none", cursor:"pointer", color:C.subtle, fontSize:16, lineHeight:1, padding:0 }}>×</button>
                </div>
              )}

              <div onClick={()=>fileRef.current.click()}
                onDragOver={e=>{e.preventDefault();setDragging(true);}}
                onDragLeave={()=>setDragging(false)}
                onDrop={onDrop}
                style={{ border:`1.5px dashed ${dragging||invoiceFile?C.indigo:C.border}`, borderRadius:8, padding:"18px 16px", textAlign:"center", cursor:"pointer", background:dragging||invoiceFile?C.indigoL:C.bg, transition:"all 0.15s" }}>
                <input ref={fileRef} type="file" accept="application/pdf" style={{ display:"none" }} onChange={e=>setInvoiceFile(e.target.files[0]||null)}/>
                {invoiceFile ? (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.indigo} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span style={{ fontSize:13, color:C.indigo, fontWeight:600 }}>{invoiceFile.name}</span>
                    <button onClick={e=>{e.stopPropagation();setInvoiceFile(null);}} style={{ background:"none", border:"none", cursor:"pointer", color:C.subtle, fontSize:18, lineHeight:1, padding:0 }}>×</button>
                  </div>
                ) : (
                  <div>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.subtle} strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom:5 }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    <p style={{ margin:0, fontSize:13, color:C.muted }}>Trage PDF sau <span style={{ color:C.indigo, fontWeight:600 }}>alege fișier</span></p>
                  </div>
                )}
              </div>
            </div>

            {/* SINGLE CTA */}
            <button onClick={handleSaveBilling} disabled={savingBill}
              style={{ width:"100%", padding:"11px", borderRadius:7, border:"none", background:savingBill?C.subtle:C.indigo, color:"#fff", fontSize:14, fontWeight:600, cursor:savingBill?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, transition:"background 0.15s" }}>
              {savingBill
                ? <><Spin/> Se salvează...</>
                : invoiceFile ? "Salvează factura + încarcă PDF" : "Salvează factura"}
            </button>
          </Card>

          {/* CARD: Istoric */}
          <Card>
            <CardHeader title="Istoric plăți" badge={r.paymentHistory?.length||0}/>
            {!r.paymentHistory?.length ? (
              <p style={{ color:C.subtle, fontSize:13, textAlign:"center", padding:"16px 0", margin:0 }}>Niciun istoric disponibil.</p>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr>
                    {["Sumă","Scadent","Plătit","Status","PDF"].map(h=>(
                      <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, textTransform:"uppercase", letterSpacing:"0.5px", color:C.subtle, fontWeight:700, borderBottom:`1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {r.paymentHistory.slice().reverse().map((h,i)=>{
                    const hs=STATUS_STYLE[h.status]||STATUS_STYLE.pending;
                    return (
                      <tr key={i} className="row-hover" style={{ borderBottom:`1px solid ${C.border}`, transition:"background 0.1s" }}>
                        <td style={{ padding:"11px 10px", fontWeight:700, color:C.text }}>{fmtCur(h.amount,h.currency)}</td>
                        <td style={{ padding:"11px 10px", color:C.muted }}>{fmt(h.dueDate)}</td>
                        <td style={{ padding:"11px 10px", color:C.muted }}>{h.paidAt?fmt(h.paidAt):"—"}</td>
                        <td style={{ padding:"11px 10px" }}>
                          <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:99, background:hs.bg, color:hs.color, border:`1px solid ${hs.border}` }}>{hs.label}</span>
                        </td>
                        <td style={{ padding:"11px 10px" }}>
                          {h.invoiceUrl
                            ? <a href={h.invoiceUrl} target="_blank" rel="noreferrer" style={{ color:C.indigo, fontWeight:600, textDecoration:"none", fontSize:12 }}>↓ PDF</a>
                            : <span style={{ color:C.subtle }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>

          {/* CARD: Note */}
          <Card>
            <CardHeader title="Note interne" badge={r.notes?.length||null}/>
            <div style={{ display:"flex", gap:8, marginTop:2, marginBottom:r.notes?.length?14:0 }}>
              <input value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Adaugă o notă internă..." style={{ ...INP, flex:1 }}
                onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); handleAddNote(); }}}/>
              <button onClick={handleAddNote} disabled={!noteText.trim()||savingNote}
                style={{ padding:"9px 16px", borderRadius:7, border:"none", background:noteText.trim()?C.indigo:C.border, color:noteText.trim()?"#fff":C.subtle, fontWeight:600, fontSize:13, cursor:noteText.trim()?"pointer":"not-allowed", whiteSpace:"nowrap" }}>
                {savingNote?"...":"+ Adaugă"}
              </button>
            </div>
            {r.notes?.slice().reverse().map((n,i)=>(
              <div key={n._id||i} style={{ display:"flex", gap:10, padding:"11px 13px", borderRadius:8, background:C.bg, border:`1px solid ${C.border}`, marginBottom:8 }}>
                <div style={{ width:26, height:26, borderRadius:"50%", background:C.indigoL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:C.indigo, flexShrink:0 }}>A</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:"0 0 4px", fontSize:11, color:C.subtle }}>{fmtLong(n.createdAt)}</p>
                  <p style={{ margin:0, fontSize:13, color:C.text, lineHeight:1.6, whiteSpace:"pre-wrap" }}>{n.text}</p>
                </div>
                <button onClick={()=>handleDeleteNote(n._id)}
                  style={{ background:"none", border:"none", cursor:"pointer", color:C.subtle, fontSize:16, lineHeight:1, flexShrink:0, padding:0 }}
                  onMouseEnter={e=>e.currentTarget.style.color=C.red}
                  onMouseLeave={e=>e.currentTarget.style.color=C.subtle}>×</button>
              </div>
            ))}
          </Card>
        </div>

        {/* RIGHT SIDEBAR */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* Sumar client */}
          <Card>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
              <div style={{ width:42, height:42, borderRadius:10, background:planObj.bg, border:`1.5px solid ${planObj.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, fontWeight:800, color:planObj.color, flexShrink:0 }}>
                {r.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p style={{ margin:0, fontWeight:700, color:C.text, fontSize:14 }}>{r.name}</p>
                <p style={{ margin:0, fontSize:12, color:C.muted }}>{r.slug}.orderly.ro</p>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {[
                ["Status", <StatusBadge s={ss}/>],
                ["Plan",   <span style={{ fontWeight:700, color:planObj.color, fontSize:13 }}>{planObj.name}</span>],
                ["Sumă curentă", <span style={{ fontWeight:700, color:C.text, fontSize:13 }}>{fmtCur(r.currentAmount, r.currentCurrency)}</span>],
                ["Scadent", <span style={{ fontSize:13, color:C.text }}>{fmtLong(r.currentDueDate)}</span>],
                info.contractEnd && ["Contract", <span style={{ fontSize:12, color:new Date(info.contractEnd)<new Date()?C.red:C.muted }}>{fmt(info.contractStart)} → {fmt(info.contractEnd)}</span>],
                ["Activ", <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:99, background:r.isActive?C.greenL:C.redL, color:r.isActive?C.green:C.red, border:`1px solid ${r.isActive?"#b3ddd1":"#f5c6cb"}` }}>{r.isActive?"Da":"Nu"}</span>],
              ].filter(Boolean).map(([label, val], i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                  <span style={{ fontSize:12, color:C.muted }}>{label}</span>
                  {val}
                </div>
              ))}
            </div>
          </Card>

          {/* Informații restaurant */}
          <Accordion title="Informații restaurant" open={openSection==="info"} onToggle={()=>setOpenSection(p=>p==="info"?null:"info")}>
            <Label>Plan</Label>
            <div style={{ display:"flex", gap:6, marginBottom:14 }}>
              {PLANS.map(pid=>{
                const p=PLAN_PRICING[pid];
                return (
                  <button key={pid} onClick={()=>setI("plan",pid)}
                    style={{ flex:1, padding:"8px 4px", borderRadius:7, border:`1.5px solid ${info.plan===pid?p.color:C.border}`, background:info.plan===pid?p.bg:C.white, cursor:"pointer" }}>
                    <div style={{ fontSize:11, fontWeight:800, color:info.plan===pid?p.color:C.muted }}>{p.name}</div>
                    <div style={{ fontSize:10, color:C.subtle, marginTop:2 }}>€{p.monthly}/lun</div>
                  </button>
                );
              })}
            </div>

            <Label>Perioadă</Label>
            <div style={{ display:"flex", gap:6, marginBottom:14 }}>
              {[["monthly","Lunar"],["quarterly","Trim."],["yearly","Anual"]].map(([v,l])=>(
                <button key={v} onClick={()=>setI("billingPeriod",v)}
                  style={{ flex:1, padding:"7px", borderRadius:7, border:`1.5px solid ${info.billingPeriod===v?C.indigo:C.border}`, background:info.billingPeriod===v?C.indigoL:C.white, color:info.billingPeriod===v?C.indigo:C.muted, fontWeight:600, fontSize:11, cursor:"pointer" }}>
                  {l}
                </button>
              ))}
            </div>

            <div style={{ height:1, background:C.border, margin:"4px 0 14px" }}/>

            <Label>Reducere</Label>
            <div style={{ display:"flex", gap:6, marginBottom:8 }}>
              {[["none","Fără"],["percent","%"],["fixed","Fix"]].map(([v,l])=>(
                <button key={v} onClick={()=>setI("discountType",v)}
                  style={{ flex:1, padding:"7px", borderRadius:7, border:`1.5px solid ${info.discountType===v?C.amber:C.border}`, background:info.discountType===v?C.amberL:C.white, color:info.discountType===v?C.amber:C.muted, fontWeight:600, fontSize:11, cursor:"pointer" }}>
                  {l}
                </button>
              ))}
            </div>
            {info.discountType!=="none" && (
              <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:14 }}>
                <input type="number" value={info.discountValue} onChange={e=>setI("discountValue",Number(e.target.value))} placeholder="Valoare" style={INP}/>
                <input value={info.discountNote} onChange={e=>setI("discountNote",e.target.value)} placeholder="Motiv" style={INP}/>
                <input type="date" value={info.discountExpiry} onChange={e=>setI("discountExpiry",e.target.value)} style={INP}/>
              </div>
            )}

            <div style={{ height:1, background:C.border, margin:"4px 0 14px" }}/>

            <Label>Contact & companie</Label>
            <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:14 }}>
              {[["contactPerson","Persoană contact"],["contactEmail","Email"],["contactPhone","Telefon"],["companyName","Companie"],["vatNumber","CUI / VAT"],["address","Adresă"]].map(([k,ph])=>(
                <input key={k} value={info[k]} onChange={e=>setI(k,e.target.value)} placeholder={ph} style={INP}/>
              ))}
            </div>

            <div style={{ height:1, background:C.border, margin:"4px 0 14px" }}/>

            <Label>Contract</Label>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7, marginBottom:14 }}>
              <input type="date" value={info.contractStart} onChange={e=>setI("contractStart",e.target.value)} style={INP} title="Start"/>
              <input type="date" value={info.contractEnd} onChange={e=>setI("contractEnd",e.target.value)} style={INP} title="Expirare"/>
            </div>

            <Label>URL-uri</Label>
            <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:16 }}>
              {[["frontendUrl","Frontend"],["adminUrl","Admin"],["backendUrl","API"]].map(([k,ph])=>(
                <div key={k} style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <input value={info[k]} onChange={e=>setI(k,e.target.value)} placeholder={ph} style={{ ...INP, flex:1 }}/>
                  {info[k] && <a href={info[k]} target="_blank" rel="noreferrer" style={{ color:C.indigo, textDecoration:"none", fontSize:14 }}>↗</a>}
                </div>
              ))}
            </div>

            <button onClick={handleSaveInfo} disabled={savingInfo}
              style={{ width:"100%", padding:"10px", borderRadius:7, border:"none", background:savingInfo?C.subtle:C.indigo, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
              {savingInfo?<><Spin/> Salvez...</>:"Salvează informații"}
            </button>
          </Accordion>
        </div>
      </div>
    </div>
  );
}

/* ── helpers ─────────────────────────────────────────────── */
function Card({ children }) {
  return <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e3e8ef", padding:"22px 24px" }}>{children}</div>;
}
function CardHeader({ title, badge }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, paddingBottom:16, borderBottom:"1px solid #e3e8ef" }}>
      <h2 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0a2540" }}>{title}</h2>
      {badge != null && badge > 0 && (
        <span style={{ fontSize:11, fontWeight:700, padding:"1px 8px", borderRadius:99, background:"#f0efff", color:"#635bff" }}>{badge}</span>
      )}
    </div>
  );
}
function Accordion({ title, open, onToggle, children }) {
  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e3e8ef", overflow:"hidden" }}>
      <button onClick={onToggle} style={{ display:"flex", alignItems:"center", width:"100%", padding:"15px 20px", background:"none", border:"none", cursor:"pointer", gap:8 }}>
        <span style={{ fontSize:13, fontWeight:700, color:"#0a2540", flex:1, textAlign:"left" }}>{title}</span>
        <svg style={{ transform:open?"rotate(180deg)":"none", transition:"transform 0.2s", color:"#a3acb9" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && <div style={{ padding:"0 20px 20px", borderTop:"1px solid #e3e8ef" }}><div style={{ paddingTop:16 }}>{children}</div></div>}
    </div>
  );
}
function StatusBadge({ s }) {
  return <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:99, background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>{s.label}</span>;
}
function Label({ children }) {
  return <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>{children}</label>;
}
function Spin({ color="#fff" }) {
  return <svg style={{ animation:"spin 0.8s linear infinite", flexShrink:0 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>;
}
